import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Menu, Grid } from "antd";
import Icon from "../util-components/Icon";
import navigationConfig from "configs/NavigationConfig";
import { connect } from "react-redux";
import { SIDE_NAV_LIGHT, NAV_TYPE_SIDE } from "constants/ThemeConstant";
import utils from "utils";
import { onMobileNavToggle } from "redux/actions/Theme";
import useAuth from "hooks/useAuth";

const { SubMenu } = Menu;
const { useBreakpoint } = Grid;

const setLocale = (isLocaleOn, localeKey) => localeKey.toString();

const setDefaultOpen = (key) => {
  let keyList = [];
  let keyString = "";
  if (key) {
    const arr = key.split("-");
    for (let index = 0; index < arr.length; index++) {
      const elm = arr[index];
      index === 0 ? (keyString = elm) : (keyString = `${keyString}-${elm}`);
      keyList.push(keyString);
    }
  }
  return keyList;
};

/**
 * Filter navigation items based on user permissions
 * @param {Array} navItems - Navigation items to filter
 * @param {Function} hasPermission - Function to check if user has permission
 * @returns {Array} - Filtered navigation items
 */
const filterNavByPermission = (navItems, hasPermission) => {
  return navItems
    .filter((item) => {
      // If no permission defined, show the item (backward compatibility)
      if (!item.permission || item.permission.length === 0) {
        return true;
      }
      // Check if user has any of the required permissions
      return hasPermission(item.permission);
    })
    .map((item) => {
      // Recursively filter submenus
      if (item.submenu && item.submenu.length > 0) {
        return {
          ...item,
          submenu: filterNavByPermission(item.submenu, hasPermission),
        };
      }
      return item;
    })
    // Remove items that have empty submenus after filtering (for parent menu groups)
    .filter((item) => {
      if (item.submenu && item.submenu.length === 0 && !item.path) {
        return false;
      }
      return true;
    });
};

const SideNavContent = (props) => {
  const { sideNavTheme, routeInfo, hideGroupTitle, localization, onMobileNavToggle } = props;

  const { hasPermission } = useAuth([]);

  // Filter navigation based on user permissions
  const filteredNavigation = useMemo(() => {
    return filterNavByPermission(navigationConfig, hasPermission);
  }, [hasPermission]);

  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes("lg");
  
  const closeMobileNav = () => {
    if (isMobile) {
      onMobileNavToggle(false);
    }
  };

  const renderMenuItem = (item) => (
    <Menu.Item key={item.key}>
      {item.icon ? <Icon type={item.icon} /> : null}
      <span>{setLocale(localization, item.title)}</span>
      {item.path ? <Link onClick={() => closeMobileNav()} to={item.path} /> : null}
    </Menu.Item>
  );

  const renderSubMenu = (item) => (
    <SubMenu
      key={item.key}
      icon={item.icon ? <Icon type={item.icon} /> : null}
      title={setLocale(localization, item.title)}
    >
      {item.submenu.map((subItem) =>
        subItem.submenu && subItem.submenu.length > 0
          ? renderSubMenu(subItem)
          : renderMenuItem(subItem)
      )}
    </SubMenu>
  );

  const renderMenuGroup = (menu) => (
    <Menu.ItemGroup key={menu.key} title={setLocale(localization, menu.title)}>
      {menu.submenu.map((subMenuFirst) =>
        subMenuFirst.submenu && subMenuFirst.submenu.length > 0
          ? renderSubMenu(subMenuFirst)
          : renderMenuItem(subMenuFirst)
      )}
    </Menu.ItemGroup>
  );

  return (
    <Menu
      theme={sideNavTheme === SIDE_NAV_LIGHT ? "light" : "dark"}
      mode="inline"
      style={{ height: "100%", borderRight: 0 }}
      defaultSelectedKeys={[routeInfo?.key]}
      defaultOpenKeys={setDefaultOpen(routeInfo?.key)}
      className={hideGroupTitle ? "hide-group-title" : ""}
    >
      {filteredNavigation.map((menu) =>
        menu.submenu && menu.submenu.length > 0
          ? renderMenuGroup(menu)
          : renderMenuItem(menu)
      )}
    </Menu>
  );
};

const TopNavContent = (props) => {
  const { topNavColor, localization } = props;
  const { hasPermission } = useAuth([]);

  // Filter navigation based on user permissions
  const filteredNavigation = useMemo(() => {
    return filterNavByPermission(navigationConfig, hasPermission);
  }, [hasPermission]);

  const renderMenuItem = (item) => (
    <Menu.Item key={item.key}>
      {item.icon ? <Icon type={item.icon} /> : null}
      <span>{setLocale(localization, item.title)}</span>
      {item.path ? <Link to={item.path} /> : null}
    </Menu.Item>
  );

  const renderSubMenu = (item) => (
    <SubMenu
      key={item.key}
      popupClassName="top-nav-menu"
      title={
        <span>
          {item.icon ? <Icon type={item.icon} /> : null}
          <span>{setLocale(localization, item.title)}</span>
        </span>
      }
    >
      {item.submenu.map((subItem) =>
        subItem.submenu && subItem.submenu.length > 0
          ? renderSubMenu(subItem)
          : renderMenuItem(subItem)
      )}
    </SubMenu>
  );

  return (
    <Menu mode="horizontal" style={{ backgroundColor: topNavColor }}>
      {filteredNavigation.map((menu) =>
        menu.submenu && menu.submenu.length > 0
          ? renderSubMenu(menu)
          : renderMenuItem(menu)
      )}
    </Menu>
  );
};

const MenuContent = (props) => {
  return props.type === NAV_TYPE_SIDE ? (
    <SideNavContent {...props} />
  ) : (
    <TopNavContent {...props} />
  );
};

const mapStateToProps = ({ theme }) => {
  const { sideNavTheme, topNavColor } = theme;
  return { sideNavTheme, topNavColor };
};

export default connect(mapStateToProps, { onMobileNavToggle })(MenuContent);
